import React, { useState, useEffect, useCallback } from "react";
import apiClient from "../../../../api/apiClient";
import Loading from "../../../../components/Loading/Loading";
import CompanyFormModal from "./modals/CompanyFormModal";
import "./CompaniesTab.css";

const BUSINESS_TYPE_LABELS = { retail: "Retail", distributor: "Distributor", cultivator: "Cultivator" };
const INDUSTRY_LABELS      = { hemp: "Hemp", rec_adult_use: "Rec / Adult Use", medical: "Medical" };

const CompaniesTab = ({ onSelectCompany }) => {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading]     = useState(false);
    const [search, setSearch]       = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [showForm, setShowForm]   = useState(false);
    const [importing, setImporting] = useState(false);

    const fetchCompanies = useCallback(async (q = "") => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (q) params.set("search", q);
            const res = await apiClient.get(`/admin/crm/companies?${params}`);
            setCompanies(res.data.companies || []);
        } catch (err) {
            console.error("[CompaniesTab] fetch error:", err);
        } finally {
            setLoading(false);
        }
    }, []);
    
    useEffect(() => { fetchCompanies(search); }, [search, fetchCompanies]);

    const handleImport = async () => {
        setImporting(true);
        try {
            const res = await apiClient.post("/admin/crm/companies/import-from-leads");
            await fetchCompanies(search);
            alert(res.data.message);
        } catch (err) {
            alert(err.response?.data?.error || "Import failed");
        } finally {
            setImporting(false);
        }
    };

    const handleSearchKey = (e) => {
        if (e.key === "Enter") setSearch(searchInput.trim());
    };

    const handleSearchChange = (e) => {
        setSearchInput(e.target.value);
        if (e.target.value === "") setSearch("");
    };

    return (
        <div className="companies-tab">
            <div className="companies-toolbar">
                <input
                    className="companies-search"
                    type="text"
                    placeholder="Search companies… (Enter)"
                    value={searchInput}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKey}
                />
                <span className="companies-count">
                    {companies.length} {companies.length === 1 ? "company" : "companies"}
                </span>
                <button
                    className="admin-btn"
                    onClick={handleImport}
                    disabled={importing}
                    title="Auto-create companies from existing outbound lead company names"
                >
                    {importing ? "Importing…" : "↑ Import from Leads"}
                </button>
                <button className="admin-btn admin-btn--primary" onClick={() => setShowForm(true)}>
                    + New Company
                </button>
            </div>

            {loading ? (
                <Loading />
            ) : companies.length === 0 ? (
                <div className="companies-empty">
                    <p>No companies found.</p>
                    <p>Use <strong>↑ Import from Leads</strong> to pull in all company names from your existing outbound leads, or <strong>+ New Company</strong> to add one manually.</p>
                </div>
            ) : (
                <table className="companies-table">
                    <thead>
                        <tr>
                            <th>Business Name</th>
                            <th>Type</th>
                            <th>Industry</th>
                            <th>License</th>
                            <th>Location</th>
                            <th>Contacts</th>
                        </tr>
                    </thead>
                    <tbody>
                        {companies.map((c) => (
                            <tr
                                key={c.id}
                                className="company-row company-row--clickable"
                                onClick={() => onSelectCompany(c.id)}
                            >
                                <td className="company-name-cell">{c.businessName}</td>
                                <td>
                                    <span className={`co-type-badge co-type--${c.businessType}`}>
                                        {BUSINESS_TYPE_LABELS[c.businessType] || c.businessType}
                                    </span>
                                </td>
                                <td>
                                    <span className={`co-industry-badge co-industry--${c.industryType}`}>
                                        {INDUSTRY_LABELS[c.industryType] || c.industryType}
                                    </span>
                                </td>
                                <td className="company-muted">
                                    {c.licenseNumber || "—"}
                                </td>
                                <td className="company-muted">
                                    {[c.city, c.state].filter(Boolean).join(", ") || "—"}
                                </td>
                                <td>
                                    <span className="company-lead-count">{c.contactCount ?? 0}</span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {showForm && (
                <CompanyFormModal
                    onClose={() => setShowForm(false)}
                    onSaved={() => { setShowForm(false); fetchCompanies(search); }}
                />
            )}
        </div>
    );
};

export default CompaniesTab;
